/**
 * AdminProjectsPage — project & membership rosters across the org.
 *
 * Two views over the same projects-with-members snapshot, toggled in place:
 *   - "Projects": drill into one project's roster. WRITE surface under RBAC
 *                 v2.1 (the admin tier may add / remove / re-role any member and
 *                 reassign the owner org-wide — the backend authorizes it even
 *                 though admin 404s on project DATA routes, so this admin area is
 *                 the only place an admin can manage membership). Mutations call
 *                 refetch to re-pull the snapshot.
 *   - "Matrix":   a read-only user x project grid (observation only).
 *
 * A 403 from the read endpoint (org-role member) yields an access-restricted
 * empty state.
 *
 * Mirrors AdminUsersPage shell conventions: shadcn Card/Input/Select, Search
 * icon, sonner-style loading via Loader2, in-page search/filter.
 */
import React, { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Search, Loader2, ShieldAlert } from 'lucide-react';
import { ACCESS_INTENT } from '../../lib/auth';
import { useAdminProjectsWithMembers } from '../../hooks/useAdminProjectsWithMembers';
import ProjectsListView from './projects/ProjectsListView';
import MatrixView from './projects/MatrixView';

const VIEW = { PROJECTS: 'projects', MATRIX: 'matrix' };

const AdminProjectsPage = () => {
  const { projects, loading, intent, refetch } = useAdminProjectsWithMembers();
  const [view, setView] = useState(VIEW.PROJECTS);
  const [searchQuery, setSearchQuery] = useState('');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // 403 (org-role member hit the admin-only endpoint) => restricted.
  if (intent === ACCESS_INTENT.DISABLE || intent === ACCESS_INTENT.HIDE) {
    return (
      <div className="space-y-6" data-testid="admin-projects-page">
        <Card className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <ShieldAlert className="h-7 w-7 text-red-400" />
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-1">
            Access restricted
          </h2>
          <p className="text-sm text-slate-500 max-w-sm">
            You need an organization admin role to view project membership
            rosters.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-projects-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900">Projects</h1>
          <Badge variant="outline" className="text-slate-500">
            {projects.length} total
          </Badge>
        </div>
        {/* View toggle */}
        <div
          className="inline-flex rounded-md border border-slate-200 p-0.5"
          data-testid="view-toggle"
        >
          <Button
            variant={view === VIEW.PROJECTS ? 'default' : 'ghost'}
            size="sm"
            className="h-8"
            onClick={() => setView(VIEW.PROJECTS)}
            data-testid="view-toggle-projects"
          >
            Projects
          </Button>
          <Button
            variant={view === VIEW.MATRIX ? 'default' : 'ghost'}
            size="sm"
            className="h-8"
            onClick={() => setView(VIEW.MATRIX)}
            data-testid="view-toggle-matrix"
          >
            Matrix
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder={
            view === VIEW.PROJECTS
              ? 'Search by project name or code...'
              : 'Search by user name or email...'
          }
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 h-9"
          data-testid="projects-search-input"
        />
      </div>

      {/* Body */}
      {view === VIEW.PROJECTS ? (
        <ProjectsListView
          projects={projects}
          searchQuery={searchQuery}
          refetch={refetch}
        />
      ) : (
        <Card className="p-0">
          <MatrixView projects={projects} searchQuery={searchQuery} />
        </Card>
      )}
    </div>
  );
};

export default AdminProjectsPage;
